// BCM-898 · Goods Receipt Dashboard — lazy per-batch detail hook.
//
// A2 fetchDeliveryScheduleBatchDetails, fetched only when a row is expanded and
// cached per batch for the session (only one row open at a time). Supplies the
// progressBar (Tier 3 of the gate) used to disambiguate PF-ambiguous statuses.
// progressBar can be absent — computeProgressBar is non-blocking (handle it).
import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { fetchBatchDetails } from "../api/goodsReceiptApi";
import { GENERIC_ERROR } from "../utils/constants";

export default function useBatchDetails() {
  const [detail, setDetail] = useState(null);
  const [detailFor, setDetailFor] = useState(null); // id the current `detail` belongs to
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef({}); // schrockenDeliveryScheduleBatchID -> detail
  const mountedRef = useRef(true);
  const latestIdRef = useRef(null); // guards against out-of-order responses
  const cancelRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving GR batch detail");
    };
  }, []);

  const load = useCallback((row) => {
    if (!row || !row.schrockenDeliveryScheduleBatchID) return Promise.resolve(null);
    const id = row.schrockenDeliveryScheduleBatchID;
    latestIdRef.current = id;
    if (cacheRef.current[id]) {
      setDetail(cacheRef.current[id]);
      setDetailFor(id);
      setError(null);
      return Promise.resolve(cacheRef.current[id]);
    }
    setLoading(true);
    setError(null);
    setDetail(null);
    setDetailFor(id);
    if (cancelRef.current) cancelRef.current.cancel("superseded batch detail fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    return fetchBatchDetails(
      {
        schrockenDeliveryScheduleBatchID: id,
        poNumber: row.scheduleAgreementNumber,
        batchNumber: row.vendorBatchNumber,
      },
      source.token
    )
      .then((res) => {
        // Always cache; only touch state if still mounted and not superseded by a
        // later expand (stale-response race would otherwise show the wrong batch).
        if (res.data && res.data.status) {
          cacheRef.current[id] = res.data.data;
          if (mountedRef.current && latestIdRef.current === id) {
            setDetail(res.data.data);
            setLoading(false);
          }
          return res.data.data;
        }
        if (mountedRef.current && latestIdRef.current === id) {
          // A2 error shape is { status:false, error } — that `error` is a raw
          // backend/exception string (e.g. "Error: connect ECONNREFUSED
          // 127.0.0.1:5001"), not user-facing copy, so show the same generic
          // toast copy every other action on this dashboard uses instead.
          setError(GENERIC_ERROR);
          setLoading(false);
        }
        return null;
      })
      .catch((err) => {
        if (axios.isCancel(err)) return null;
        if (mountedRef.current && latestIdRef.current === id) {
          setError(GENERIC_ERROR);
          setLoading(false);
        }
        return null;
      });
  }, []);

  // Drop a cached detail so the next expand re-reads it (after a sync/reset/action).
  const invalidate = useCallback((id) => {
    if (id) delete cacheRef.current[id];
    else cacheRef.current = {};
  }, []);

  return { detail, detailFor, loading, error, load, invalidate };
}
